import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import useAuth from '../../hooks/useAuth';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import {
  ReferrerDetailInfo,
  ReferralItem,
} from './models/referralModel';
import {
  getAdminReferralByReferrerEndpoint,
  updateReferralStatusEndpoint,
  getAssignedUnitByUserEndpoint,
  getRentalContractByTenantEndpoint,
  getPublishedPropertyByLandlordEndpoint,
  registerReferralProspectEndpoint,
} from './services/referralService';
import ReferrerHeaderCard from './components/referrals/ReferrerHeaderCard';
import ReferrerProspectsTable from './components/referrals/ReferrerProspectsTable';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import { IconArrowLeft } from '@tabler/icons-react';
import SweetAlert from 'react-bootstrap-sweetalert';

const DEFAULT_REFERRER: ReferrerDetailInfo = {
  id: '',
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  total_referrals: 0,
  total_paid: 0,
};

const InvestorReferralDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();

  const [activeTab, setActiveTab] = useState<'referrals' | 'payments'>('referrals');
  const [referrer, setReferrer] = useState<ReferrerDetailInfo>(DEFAULT_REFERRER);
  const [prospects, setProspects] = useState<ReferralItem[]>([]);
  const [isUpdatingId, setIsUpdatingId] = useState<string | null>(null);
  const [registeringProspectId, setRegisteringProspectId] = useState<string | null>(null);

  // Estado para la alerta SweetAlert
  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({
    title: '',
    onConfirm: () => setSweetAlert(false),
  });

  // Carga de los detalles del referidor y sus prospectos
  const loadReferrerDetail = useCallback(async () => {
    if (!id) return;
    try {
      const { status, data } = await callEndpoint(
        getAdminReferralByReferrerEndpoint(id, token),
      );
      if (status === 200) {
        const response = fetchResponseAdapter(data);
        const responseData = response.data || {};
        setReferrer(responseData.referrer || DEFAULT_REFERRER);
        setProspects(responseData.prospects || []);
      }
    } catch (error) {
      console.error('Error loading referrer detail:', error);
    }
  }, [callEndpoint, id, token]);

  useEffect(() => {
    loadReferrerDetail();
  }, [loadReferrerDetail]);

  // Registrar y vincular perfil del prospecto
  const handleRegisterProspect = async (item: ReferralItem) => {
    try {
      setRegisteringProspectId(item.id);
      const { status, data } = await callEndpoint(
        registerReferralProspectEndpoint(item.id, token),
      );
      if (status === 200 || status === 201) {
        const response = fetchResponseAdapter(data);
        const updatedReferral = response.data;
        const newProfileId = updatedReferral?.referred_profile_id;

        setProspects((prevProspects) =>
          prevProspects.map((ref) =>
            ref.id === item.id
              ? {
                ...ref,
                referred_profile_id: newProfileId || 'registered',
                referredProfile:
                  updatedReferral?.referredProfile || ref.referredProfile,
                landlord_id: updatedReferral?.landlord_id || ref.landlord_id,
                tenant_id: updatedReferral?.tenant_id || ref.tenant_id,
              }
              : ref,
          ),
        );

        await loadReferrerDetail();

        const fullName = `${item.referred_first_name} ${item.referred_last_name}`.trim();
        setAlertProps({
          success: true,
          title: 'Cuenta creada con éxito',
          text: `Se ha registrado y vinculado exitosamente la cuenta de usuario para ${fullName}.`,
          confirmBtnBsStyle: 'success',
          confirmBtnText: 'Aceptar',
          showCancel: false,
          onConfirm: () => setSweetAlert(false),
        });
        setSweetAlert(true);
      } else {
        setAlertProps({
          danger: true,
          title: 'Error al crear cuenta',
          text: data?.message || 'No se pudo registrar la cuenta para el prospecto.',
          confirmBtnBsStyle: 'danger',
          confirmBtnText: 'Entiendo',
          showCancel: false,
          onConfirm: () => setSweetAlert(false),
        });
        setSweetAlert(true);
      }
    } catch (error: any) {
      console.error('Error registering prospect:', error);
      setAlertProps({
        danger: true,
        title: 'Error al crear cuenta',
        text:
          error?.response?.data?.message ||
          'Ocurrió un problema al intentar crear la cuenta.',
        confirmBtnBsStyle: 'danger',
        confirmBtnText: 'Entiendo',
        showCancel: false,
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    } finally {
      setRegisteringProspectId(null);
    }
  };

  // Actualización del estado de un referido
  const handleUpdateStatus = async (referralId: string, newStatus: string) => {
    try {
      setIsUpdatingId(referralId);

      let extraData: { deal_amount?: number; unit_id?: string } | undefined;

      if (newStatus === 'successful') {
        const prospect = prospects.find((p) => p.id === referralId);
        if (prospect?.line?.toLowerCase() === 'buyer') {
          const buyerLandlordId = prospect?.landlord_id;

          if (!buyerLandlordId) {
            setAlertProps({
              danger: true,
              title: 'Requisitos pendientes para cerrar caso',
              text: 'El cliente referido no tiene un perfil de comprador asociado.',
              confirmBtnBsStyle: 'danger',
              confirmBtnText: 'Entiendo',
              onConfirm: () => setSweetAlert(false),
            });
            setSweetAlert(true);
            return;
          }

          const { status: psStatus, data: psData } = await callEndpoint(
            getAssignedUnitByUserEndpoint(
              buyerLandlordId,
              prospect.created_at,
              token,
            ),
          );

          if (psStatus !== 200) {
            let errorMsg = 'No se pudo verificar la unidad en pre-ventas.';

            if (
              psData?.code === 2104 ||
              psData?.message?.includes('Signing promise date')
            ) {
              errorMsg =
                'La fecha de firma de la promesa debe ser posterior a la fecha de creación del referido.';
            } else if (
              psData?.code === 2101 ||
              psData?.message?.includes('Model unit not found')
            ) {
              errorMsg =
                'El cliente referido no tiene una unidad asignada en estado de firma de promesa.';
            } else if (psData?.message) {
              errorMsg = psData.message;
            }

            setAlertProps({
              danger: true,
              title: 'Requisitos pendientes para cerrar caso',
              text: errorMsg,
              confirmBtnBsStyle: 'danger',
              confirmBtnText: 'Entiendo',
              onConfirm: () => setSweetAlert(false),
            });
            setSweetAlert(true);
            return;
          }

          const unitInfo = fetchResponseAdapter(psData).data;
          extraData = {
            deal_amount: unitInfo?.amount,
            unit_id: unitInfo?.unit_id,
          };
        } else if (prospect?.line?.toLowerCase() === 'tenant') {
          const tenantId =
            prospect?.tenant_id ||
            prospect?.referred_tenant_id ||
            prospect?.referred_profile_id;

          if (!tenantId) {
            setAlertProps({
              danger: true,
              title: 'Requisitos pendientes para cerrar caso',
              text: 'El cliente referido no tiene una cuenta o perfil de inquilino asociado.',
              confirmBtnBsStyle: 'danger',
              confirmBtnText: 'Entiendo',
              onConfirm: () => setSweetAlert(false),
            });
            setSweetAlert(true);
            return;
          }

          const { status: tsStatus, data: tsData } = await callEndpoint(
            getRentalContractByTenantEndpoint(
              tenantId,
              prospect.created_at,
              token,
            ),
          );

          if (tsStatus !== 200) {
            let errorMsg = 'No se pudo verificar el contrato de renta.';

            if (
              tsData?.code === 1102 ||
              tsData?.message?.includes('Contract not found')
            ) {
              errorMsg =
                'El cliente referido no tiene ningún contrato de renta registrado en la plataforma.';
            } else if (
              tsData?.code === 1107 ||
              tsData?.message?.includes('First quota has not been paid')
            ) {
              errorMsg =
                'El contrato está registrado, pero aún no se ha confirmado el pago de la primera cuota.';
            } else if (
              tsData?.code === 1106 ||
              tsData?.message?.includes('Contract creation date') ||
              tsData?.message?.includes('Contract payment date')
            ) {
              errorMsg =
                'La fecha de creación del contrato debe ser posterior a la fecha de creación del referido.';
            } else if (tsData?.message) {
              errorMsg = tsData.message;
            }

            setAlertProps({
              danger: true,
              title: 'Requisitos pendientes para cerrar caso',
              text: errorMsg,
              confirmBtnBsStyle: 'danger',
              confirmBtnText: 'Entiendo',
              onConfirm: () => setSweetAlert(false),
            });
            setSweetAlert(true);
            return;
          }

          const contractInfo = fetchResponseAdapter(tsData).data;
          extraData = {
            deal_amount: contractInfo?.amount,
            unit_id: contractInfo?.unit_id,
          };
        } else if (
          [
            'supply_sales',
            'supply_rents',
            'full_service',
            'property',
            'external',
          ].includes(prospect?.line?.toLowerCase() || '')
        ) {
          const landlordId =
            prospect?.landlord_id || prospect?.referred_profile_id;

          if (!landlordId) {
            setAlertProps({
              danger: true,
              title: 'Requisitos pendientes para cerrar caso',
              text: 'El cliente referido no tiene una cuenta o perfil asociado.',
              confirmBtnBsStyle: 'danger',
              confirmBtnText: 'Entiendo',
              onConfirm: () => setSweetAlert(false),
            });
            setSweetAlert(true);
            return;
          }

          const { status: rsStatus, data: rsData } = await callEndpoint(
            getPublishedPropertyByLandlordEndpoint(
              landlordId,
              prospect?.created_at || '',
              token,
            ),
          );

          if (rsStatus !== 200) {
            let errorMsg = 'No se pudo verificar la propiedad publicada.';

            if (
              rsData?.code === 1101 ||
              rsData?.message?.includes('Property not found')
            ) {
              errorMsg =
                'El cliente referido no tiene ninguna propiedad publicada en la plataforma.';
            } else if (
              rsData?.code === 1106 ||
              rsData?.message?.includes('Property date not valid')
            ) {
              errorMsg =
                'La fecha de publicación de la propiedad debe ser posterior a la fecha de creación del referido.';
            } else if (rsData?.message) {
              errorMsg = rsData.message;
            }

            setAlertProps({
              danger: true,
              title: 'Requisitos pendientes para cerrar caso',
              text: errorMsg,
              confirmBtnBsStyle: 'danger',
              confirmBtnText: 'Entiendo',
              onConfirm: () => setSweetAlert(false),
            });
            setSweetAlert(true);
            return;
          }

          const propertyInfo = fetchResponseAdapter(rsData).data;
          extraData = {
            deal_amount: propertyInfo?.amount,
            unit_id: propertyInfo?.unit_id,
          };
        }
      }

      const { status, data } = await callEndpoint(
        updateReferralStatusEndpoint(referralId, newStatus, token, extraData),
      );
      if (status === 200) {
        if (newStatus === 'successful') {
          setAlertProps({
            success: true,
            title: 'Caso Exitoso — registro creado en Dashboard Pagos',
            text: 'El caso ha sido cerrado exitosamente y registrado en el Dashboard de Pagos. Finanzas puede proceder con la liquidación.',
            confirmBtnBsStyle: 'success',
            confirmBtnText: 'Aceptar',
            onConfirm: () => setSweetAlert(false),
          });
          setSweetAlert(true);
        }
        // Recargar datos actualizados
        await loadReferrerDetail();
      } else {
        const errorMsg =
          data?.message ||
          'No se pudo actualizar el estado del referido debido a que no cumple con las validaciones requeridas.';
        setAlertProps({
          danger: true,
          title:
            newStatus === 'successful'
              ? 'Requisitos pendientes para cerrar caso'
              : 'Requisito pendiente',
          text: errorMsg,
          confirmBtnBsStyle: 'danger',
          confirmBtnText: 'Entiendo',
          onConfirm: () => setSweetAlert(false),
        });
        setSweetAlert(true);
      }
    } catch (error: any) {
      console.error('Error updating referral status:', error);
      setAlertProps({
        danger: true,
        title: 'Error',
        text: error?.message || 'Ocurrió un error inesperado al actualizar el estado.',
        confirmBtnBsStyle: 'danger',
        confirmBtnText: 'Entiendo',
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    } finally {
      setIsUpdatingId(null);
    }
  };

  return (
    <AdminPage
      name="Plan de Referidos"
      parentName="Proyectos"
    >
      {sweetAlert && (
        <SweetAlert {...alertProps}>
          {alertProps.text || ''}
        </SweetAlert>
      )}
      {/* Tabs de Navegación */}
      <div className="d-flex align-items-center mb-4">
        <div
          className="p-1 d-inline-flex align-items-center"
          style={{
            backgroundColor: '#f1f5f9',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}
        >
          <button
            type="button"
            className="btn btn-sm font-weight-600"
            style={{
              borderRadius: '6px',
              padding: '7px 22px',
              fontSize: '13px',
              border: 'none',
              backgroundColor: activeTab === 'referrals' ? '#00c389' : 'transparent',
              color: activeTab === 'referrals' ? '#ffffff' : '#64748b',
              boxShadow:
                activeTab === 'referrals'
                  ? '0 2px 6px rgba(0, 195, 137, 0.3)'
                  : 'none',
              transition: 'all 0.15s ease',
            }}
            onClick={() => {
              navigate('/referrals/?tab=referrals');
            }}
          >
            <i className="fas fa-users mr-2" />
            Referidos
          </button>

          <button
            type="button"
            className="btn btn-sm font-weight-600"
            style={{
              borderRadius: '6px',
              padding: '7px 22px',
              fontSize: '13px',
              border: 'none',
              backgroundColor: activeTab === 'payments' ? '#00c389' : 'transparent',
              color: activeTab === 'payments' ? '#ffffff' : '#64748b',
              boxShadow:
                activeTab === 'payments'
                  ? '0 2px 6px rgba(0, 195, 137, 0.3)'
                  : 'none',
              transition: 'all 0.15s ease',
            }}
            onClick={() => {
              navigate('/referrals/?tab=payments');
            }}
          >
            <i className="fas fa-credit-card mr-2" />
            Dashboard Pagos
          </button>
        </div>
      </div>

      {/* Botón Volver a Referidos */}
      <div className="mb-3">
        <Button
          color="link"
          className="p-0 font-weight-600 d-inline-flex align-items-center"
          style={{
            color: '#00c389',
            fontSize: '13px',
            textDecoration: 'none',
          }}
          onClick={() => navigate('/referrals/')}
        >
          <IconArrowLeft size={16} className="mr-1" />
          Volver a Referidos
        </Button>
      </div>

      {/* Tarjeta de Cabecera del Referidor */}
      <ReferrerHeaderCard referrer={referrer} />

      {/* Tabla de Prospectos Referidos */}
      <ReferrerProspectsTable
        prospects={prospects}
        isLoading={isLoading && prospects.length === 0}
        onUpdateStatus={handleUpdateStatus}
        isUpdatingId={isUpdatingId}
        onRegisterProspect={handleRegisterProspect}
        registeringProspectId={registeringProspectId}
      />
    </AdminPage>
  );
};

export default InvestorReferralDetail;
